import React, { useState } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import CountUp from '../components/CountUp';
import Waves from '../components/Waves';
import HeroCarousel from '../components/HeroCarousel';
import ProjectCard from '../components/ProjectCard';
import ProjectDetail from '../components/ProjectDetail';
import ImageComparison from '../components/ImageComparison';
import { projectsData } from '../data/projectsData';
import heroSirnach from '../assets/efh_sirnach/1.jpg';
import heroFullinsdorf from '../assets/füllinsdorf/1.jpg';
import heroHotel from '../assets/hotel/1.jpg';
import heroInterior from '../assets/füllinsdorf/2.jpg';
import zwingen2 from '../assets/mfh_zwingen/2.jpg';
import zwingen3 from '../assets/mfh_zwingen/3.jpg';
import zwingen4 from '../assets/mfh_zwingen/4.jpg';
import andWil from '../assets/andwil/6.jpg'
import amriswil from '../assets/amriswil/1.jpg';
import './Architecture.css';

function Architecture() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setProjectDetailOpen(true);
  };

  const handleCloseProjectDetail = () => {
    setProjectDetailOpen(false);
    setSelectedProject(null);
  };
  const heroSlides = [
    {
      image: heroSirnach,
      label: 'Sirnach',
      eyebrow: 'Einfamilienhaus',
      title: 'EFH Sirnach',
      description: 'Modernes Wohnen',
      animation: 'slide-left',
      ctaText: 'Projekt ansehen',
      ctaHref: '/architecture/references'
    },
    {
      image: heroFullinsdorf,
      label: 'Füllinsdorf',
      eyebrow: 'Einfamilienhaus',
      title: 'EFH Füllinsdorf',
      description: 'Zeitgemässer Wohnbau mit grosszügigen Terrassen',
      animation: 'slide-right',
      ctaText: 'Projekt ansehen',
      ctaHref: '/architecture/references'
    },
    {
      image: andWil,
      label: 'AndWil',
      eyebrow: 'Einfamilienhaus',
      title: 'EFH AndWil',
      description: 'Einfamilienhaus mit dynamischer, metamorphischer Form und offen sichtbarer Tragstruktur',
      animation: 'zoom',
      ctaText: 'Projekt ansehen',
      ctaHref: '/architecture/references'
    },
    {
      image: amriswil,
      label: 'Amriswil',
      eyebrow: 'Mehrfamilienhaus',
      title: 'MFH Amriswil',
      description: 'Modenes Mehrfamilienhaus mit klarer Formensprache und funktionaler Gestaltung',
      animation: 'rise',
      ctaText: 'Projekt ansehen',
      ctaHref: '/architecture/references'
    }
  ];

  const stats = [
    { number: 4, suffix: '+', label: 'Jahre Erfahrung', delay: 0 },
    { number: 18, suffix: '+', label: 'Projekte in 2025', delay: 0.2 },
    { number: 10, suffix: '+', label: 'Jahre Branchenerfahrung', delay: 0.4 }
  ];

  const services = [
    {
      image: require('../pics/service-01.png'),
      title: 'ARCHITEKTUR',
      description: 'Innovative architektonische Gestaltung, die Form, Funktion und Nachhaltigkeit für modernes Wohnen vereint.'
    },
    {
      image: require('../pics/service-02.png'),
      title: 'INNENARCHITEKTUR',
      description: 'Durchdacht gestaltete Innenräume, die Ihre Persönlichkeit widerspiegeln und Komfort sowie Ästhetik maximieren.'
    },
    {
      image: require('../pics/service-03.png'),
      title: 'PLANUNG',
      description: 'Strategische Entwicklungslösungen für nachhaltige und zukunftsfähige Gemeinschaften.'
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <HeroCarousel
        className='hero-section-arch'
        slides={heroSlides}
        interval={9000}
        indicatorPosition='bottom-left'
        showActiveLabel={false}
        showSlideCaptions
        captionAlignment='center'
      />

      {/* What We Offer Section with Image */}
      <Box className='weareplannic' sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f9f9f9' }}>
        <Container maxWidth='xl'>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 6, md: 8, lg: 10 }
            }}>
            {/* Image Column */}
            <Box sx={{ flex: '1', width: '100%', maxWidth: { md: '50%' }, order: { xs: 2, md: 1 } }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}>
                <ImageComparison beforeImage={zwingen2} afterImage={zwingen3}initialPosition={50} />
              </motion.div>
            </Box>

            {/* Content Column */}
            <Box sx={{ flex: '1', width: '100%', maxWidth: { md: '50%' }, pl: { md: 4, lg: 6 }, order: { xs: 1, md: 2 } }}>
              <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                <Typography
                  variant='overline'
                  sx={{
                    color: '#999',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    letterSpacing: '3px',
                    fontWeight: 500,
                    mb: 2.5,
                    display: 'block',
                    textTransform: 'uppercase'
                  }}>
                  ÜBER UNS
                </Typography>
                <Typography
                  variant='h2'
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.5rem', lg: '2.75rem' },
                    mb: 4,
                    lineHeight: 1.3,
                    color: '#1a1a1a'
                  }}>
                  Architektur & Entwurf
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    lineHeight: 1.9,
                    color: '#6b6b6b',
                    fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                    mb: 5,
                    fontWeight: 400
                  }}>
                  Wir entwickeln durchdachte, funktionale und ästhetisch anspruchsvolle Architektur, die individuell auf die Bedürfnisse unserer
                  Bauherrschaft abgestimmt ist. Vom ersten Konzept bis zur Ausführungsplanung stehen Präzision, Klarheit und zeitlose Qualität
                  im Mittelpunkt – stets im Einklang mit Umgebung und Schweizer Bauvorschriften.
                </Typography>
                <Button
                  variant='outlined'
                  size='large'
                  href='/contact'
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderColor: '#d0d0d0',
                    color: '#333',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    position: 'relative',
                    overflow: 'hidden',
                    zIndex: 1,
                    borderRadius: 0,
                    transition: 'all 0.4s ease',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#000',
                      transition: 'left 0.4s ease',
                      zIndex: -1
                    },
                    '&:hover': {
                      borderColor: '#000',
                      color: '#fff',
                      '&::before': {
                        left: 0
                      }
                    },
                    '&:active': {
                      borderColor: '#000',
                      color: '#fff',
                      '&::before': {
                        left: 0
                      }
                    },
                    '@media (hover: none)': {
                      '&:active': {
                        borderColor: '#000',
                        color: '#fff',
                        '&::before': {
                          left: 0
                        }
                      }
                    }
                  }}>
                  Kontaktieren Sie uns →
                </Button>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Stats Section with CountUp */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#f8f8f8' }}>
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            px: { xs: 1, sm: 3, lg: 6 },
            width: '100%',
            maxWidth: '1800px',
            mx: 'auto'
          }}>
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 2, sm: 4 },
              gridTemplateColumns: {
                xs: 'repeat(3, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))'
              },
              justifyItems: 'center'
            }}>
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: stat.delay }}
                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Box
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%'
                  }}>
                  <Typography
                    variant='h2'
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '2rem', sm: '3.5rem', md: '4rem' },
                      mb: 1,
                      color: '#000'
                    }}>
                    <CountUp from={0} to={stat.number} duration={2} separator=',' />
                    {stat.suffix}
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: { xs: '1px', sm: '2px' },
                      color: '#666',
                      fontSize: { xs: '0.6rem', sm: '0.8rem', md: '0.875rem' },
                      fontWeight: 500,
                      textAlign: 'center'
                    }}>
                    {stat.label}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Services Section - Our Specialization */}
      <Box
        className='services-section-dark'
        sx={{
          py: { xs: 8, md: 12 },
          position: 'relative',
          backgroundColor: '#1a1a1a',
          overflow: 'hidden'
        }}>
        {/* Waves Background */}
        <Waves
          lineColor='rgba(255, 255, 255, 0.08)'
          backgroundColor='transparent'
          waveSpeedX={0.0125}
          waveSpeedY={0.005}
          waveAmpX={40}
          waveAmpY={20}
          xGap={12}
          yGap={35}
          friction={0.925}
          tension={0.005}
          maxCursorMove={120}
        />

        <Container maxWidth='xl' sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'center', lg: 'center' },
              gap: { xs: 6, sm: 7, md: 8, lg: 4, xl: 6 }
            }}>
            {/* Left Column - Heading */}
            <Box
              sx={{
                flex: { lg: '0 0 280px', xl: '0 0 320px' },
                textAlign: { xs: 'center', lg: 'left' },
                width: { xs: '100%', lg: 'auto' }
              }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}>
                <Typography
                  variant='overline'
                  sx={{
                    color: '#888',
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    letterSpacing: '3px',
                    fontWeight: 600,
                    mb: { xs: 2, md: 3 },
                    display: 'block'
                  }}>
                  WAS WIR TUN
                </Typography>
                <Typography
                  variant='h2'
                  sx={{
                    color: '#FFFFFF',
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.25rem', lg: '3rem', xl: '3.5rem' },
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: '-0.5px'
                  }}>
                  Unsere
                  <br />
                  Spezialisierung
                </Typography>
              </motion.div>
            </Box>

            {/* Right Column - Service Cards */}
            <Box
              sx={{
                flex: '1',
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 3, sm: 3.5, md: 4, lg: 2.5, xl: 3.5 },
                justifyContent: { xs: 'center', sm: 'center', md: 'center', lg: 'flex-start' },
                width: '100%'
              }}>
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.15 }}>
                  <Box
                    className='media-service-card'
                    sx={{
                      width: { xs: '260px', sm: '240px', md: '230px', lg: '210px', xl: '240px' },
                      height: { xs: '260px', sm: '240px', md: '230px', lg: '210px', xl: '240px' },
                      backgroundColor: 'white',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 4,
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                        '& .corner-line': {
                          width: '40px',
                          height: '40px',
                          opacity: 1
                        },
                        '& .service-image': {
                          transform: 'scale(1.08)'
                        }
                      }
                    }}>
                    {/* Top Left Corner Line */}
                    <Box
                      className='corner-line'
                      sx={{
                        position: 'absolute',
                        top: '20px',
                        left: '20px',
                        width: '25px',
                        height: '25px',
                        borderTop: '1px solid #000',
                        borderLeft: '1px solid #000',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: 0.6
                      }}
                    />

                    {/* Bottom Right Corner Line */}
                    <Box
                      className='corner-line'
                      sx={{
                        position: 'absolute',
                        bottom: '20px',
                        right: '20px',
                        width: '25px',
                        height: '25px',
                        borderBottom: '1px solid #000',
                        borderRight: '1px solid #000',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        opacity: 0.6
                      }}
                    />

                    {/* Service Icon/Image */}
                    <Box
                      component='img'
                      src={service.image}
                      alt={service.title}
                      className='service-image'
                      sx={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'contain',
                        mb: 3,
                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        filter: 'grayscale(0%)'
                      }}
                    />

                    {/* Service Title */}
                    <Typography
                      variant='h6'
                      sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        letterSpacing: '1px',
                        color: '#000',
                        textAlign: 'center',
                        textTransform: 'uppercase'
                      }}>
                      {service.title}
                    </Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Our Projects Section */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, sm: 3, lg: 6 }, width: '100%', maxWidth: '1800px', mx: 'auto' }}>
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: 'center' }}>
              <Typography
                variant='overline'
                sx={{
                  color: '#999',
                  fontSize: '0.85rem',
                  letterSpacing: '0.15em',
                  fontWeight: 600,
                  mb: 1
                }}>
                PORTFOLIO
              </Typography>
              <Typography
                variant='h3'
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontFamily: 'Jura, sans-serif',
                  letterSpacing: '0.02em'
                }}>
                Unsere Projekte
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: '#666',
                  maxWidth: '700px',
                  mx: 'auto',
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  lineHeight: 1.7,
                  fontFamily: 'Jura, sans-serif'
                }}>
                Entdecken Sie eine Auswahl unserer abgeschlossenen Projekte, die innovatives Design und architektonische Exzellenz zeigen.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gap: { xs: 1.5, sm: 2.5, md: 3 },
                gridTemplateColumns: {
                  xs: 'repeat(1, minmax(0, 1fr))',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))'
                },
                justifyItems: 'center',
                justifyContent: 'center',
                maxWidth: { xs: 360, sm: 'none' },
                width: '100%',
                mx: { xs: 'auto', sm: 0 },
                alignItems: 'stretch'
              }}>
              {projectsData.slice(0, 4).map((project) => (
                <Box
                  key={project.id}
                  sx={{
                    width: '100%'
                  }}>
                  <ProjectCard project={project} onClick={handleProjectClick} />
                </Box>
              ))}
            </Box>

            <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
              <Button
                variant='outlined'
                size='large'
                href='/architecture/references'
                sx={{
                  px: 6,
                  py: 2,
                  fontSize: '1rem',
                  color: '#000',
                  borderColor: '#000',
                  fontFamily: 'Jura, sans-serif',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  '&:hover': {
                    backgroundColor: '#000',
                    color: '#fff',
                    borderColor: '#000',
                    transform: 'scale(1.05)'
                  },
                  transition: 'all 0.3s ease'
                }}>
                Mehr Projekte laden
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Project Detail Dialog */}
      <ProjectDetail project={selectedProject} open={projectDetailOpen} onClose={handleCloseProjectDetail} />

      {/* CTA Section */}
      <Container maxWidth='lg' sx={{ py: { xs: 4, md: 4 }, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}>
          <Typography
            variant='h3'
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 3,
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
            }}>
            Bereit für Ihr Projekt?
          </Typography>
          <Typography variant='body1' paragraph sx={{ color: '#666', mb: 4, maxWidth: '600px', mx: 'auto' }}>
            Lassen Sie uns zusammenarbeiten, um Ihre architektonische Vision zu verwirklichen. Kontaktieren Sie uns noch heute für eine Beratung.
          </Typography>
          <Button
            variant='contained'
            size='large'
            href='/contact'
            sx={{
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#333',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.3s ease'
            }}>
            Kontakt aufnehmen
          </Button>
        </motion.div>
      </Container>
    </Layout>
  );
}

export default Architecture;
